import type { Rng } from '../rng';
import { drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { CasinoGame } from './types';

/**
 * 3x3 slots with five paylines: the three rows plus both diagonals. The bet
 * is split evenly across the lines (so it's always a multiple of 5), and
 * each line pays on its own from the paytable below.
 *
 * Each reel is a fixed strip built from REEL_WEIGHTS; a spin picks a stop
 * and shows that symbol plus its neighbours above and below, like a real
 * reel. Every visible cell still has the strip's symbol odds, so each line
 * returns ~97.3% (checked by exact enumeration in the tests) and the
 * whole spin does too, just with far more frequent small wins than the old
 * single-line version.
 */
export type SlotSymbol = 'SEV' | 'BAR' | 'BEL' | 'CHR' | 'LEM' | 'BLK';

export const REEL_WEIGHTS: readonly (readonly [SlotSymbol, number])[] = [
  ['SEV', 1],
  ['BAR', 3],
  ['BEL', 4],
  ['CHR', 5],
  ['LEM', 6],
  ['BLK', 13],
];

/** One reel strip: every symbol repeated by its weight, spread out so the
 *  same symbol rarely sits next to itself. Fixed order (not reshuffled per
 *  run) — a real machine's strips don't change either. */
export const REEL_STRIP: readonly SlotSymbol[] = (() => {
  const pool: SlotSymbol[] = [];
  for (const [symbol, weight] of REEL_WEIGHTS) for (let i = 0; i < weight; i++) pool.push(symbol);
  // Deterministic interleave: step through the pool with a stride coprime to
  // its length.
  const stride = 7;
  return pool.map((_, i) => pool[(i * stride) % pool.length]!);
})();

export const LINES = 5;

// Base raised 2026-09-23 from ~92.8%: Lemons x10 -> x15, mixed x5 -> x6.
// The ★ no longer changes the paytable: its luck re-spins losing spins.

/** [row per reel] for each payline, rows 0 = top ... 2 = bottom. */
export const PAYLINES: readonly { name: string; rows: readonly [number, number, number] }[] = [
  { name: 'Top', rows: [0, 0, 0] },
  { name: 'Mid', rows: [1, 1, 1] },
  { name: 'Bot', rows: [2, 2, 2] },
  { name: 'D\\', rows: [0, 1, 2] },
  { name: 'D/', rows: [2, 1, 0] },
];

const SYMBOL_LABELS: Record<SlotSymbol, string> = {
  SEV: ' 7 ',
  BAR: 'BAR',
  BEL: 'BEL',
  CHR: 'CHR',
  LEM: 'LEM',
  BLK: ' - ',
};

const HIGH: SlotSymbol[] = ['SEV', 'BAR', 'BEL'];

/** One line's return as a multiple of the per-line bet. */
export function slotPayout(line: readonly SlotSymbol[]): { multiplier: number; name: string } {
  const [a] = line;
  if (line.every((s) => s === a)) {
    if (a === 'SEV') return { multiplier: 250, name: 'Triple 7s!' };
    if (a === 'BAR') return { multiplier: 50, name: 'Three BARs' };
    if (a === 'BEL') return { multiplier: 25, name: 'Three Bells' };
    if (a === 'CHR') return { multiplier: 15, name: 'Three Cherries' };
    if (a === 'LEM') return { multiplier: 15, name: 'Three Lemons' };
  }
  if (line.every((s) => HIGH.includes(s))) return { multiplier: 6, name: 'Mixed high' };
  const cherries = line.filter((s) => s === 'CHR').length;
  if (cherries === 2) return { multiplier: 5, name: 'Two Cherries' };
  if (cherries === 1) return { multiplier: 1, name: 'One Cherry' };
  return { multiplier: 0, name: 'No win' };
}

/** grid[reel][row] */
export type SlotGrid = SlotSymbol[][];

export function spinGrid(rng: Rng): SlotGrid {
  const n = REEL_STRIP.length;
  return [0, 1, 2].map(() => {
    const stop = rng.int(n);
    return [-1, 0, 1].map((offset) => REEL_STRIP[(stop + offset + n) % n]!);
  });
}

/** A grid as the lens shows it: three rows of " 7 |BAR|BEL". */
export function gridRows(grid: SlotGrid): string[] {
  return [0, 1, 2].map((row) => ` ${[0, 1, 2].map((reel) => SYMBOL_LABELS[grid[reel]![row]!]).join('|')}`);
}

/** Where on the strip a reel shows this column (its middle symbol). */
function stopOf(column: readonly SlotSymbol[]): number {
  const n = REEL_STRIP.length;
  for (let i = 0; i < n; i++) if ([-1, 0, 1].every((o, k) => REEL_STRIP[(i + o + n) % n] === column[k])) return i;
  return 0;
}

/** The frame (tick) on which each reel stops: left to right, the last one
 *  on the result itself. */
const REEL_STOPS = [2, 4, 6] as const;

/** Frames of the reels turning and stopping, left to right, on `grid`. A
 *  reel still turning is drawn a few symbols up its strip, closing in. */
export function reelFrames(grid: SlotGrid): string[] {
  const n = REEL_STRIP.length;
  const stops = grid.map(stopOf);
  const frames: string[] = [];
  for (let f = 0; f < REEL_STOPS[2]; f++) {
    const cols = stops.map((stop, r) => {
      const behind = Math.max(0, REEL_STOPS[r]! - f) * 3;
      return [-1, 0, 1].map((o) => REEL_STRIP[(((stop - behind + o) % n) + n) % n]!);
    });
    frames.push(`${gridRows(cols).join('\n')}\nSpinning...`);
  }
  return frames;
}

export interface LineWin {
  name: string;
  multiplier: number;
}

export function lineWins(grid: SlotGrid): LineWin[] {
  const wins: LineWin[] = [];
  for (const line of PAYLINES) {
    const symbols = line.rows.map((row, reel) => grid[reel]![row]!);
    const { multiplier } = slotPayout(symbols);
    if (multiplier > 0) wins.push({ name: line.name, multiplier });
  }
  return wins;
}

/** What a spin returns per $1 bet (all five lines together). */
export function spinReturn(grid: SlotGrid): number {
  return lineWins(grid).reduce((sum, w) => sum + w.multiplier, 0) / LINES;
}

let spinOutcomes: Outcome[] | null = null;

/** Every spin's result, by checking all 32^3 reel stops once (cached): the
 *  lines share cells, so a spin's odds can't be built from one line's. */
export function slotOutcomes(): Outcome[] {
  if (spinOutcomes) return spinOutcomes;
  const n = REEL_STRIP.length;
  const counts = new Map<number, number>();
  const at = (stop: number) => [-1, 0, 1].map((o) => REEL_STRIP[(stop + o + n) % n]!);
  for (let a = 0; a < n; a++)
    for (let b = 0; b < n; b++)
      for (let c = 0; c < n; c++) {
        const r = spinReturn([at(a), at(b), at(c)]);
        counts.set(r, (counts.get(r) ?? 0) + 1);
      }
  spinOutcomes = [...counts].map(([r, k]) => [k / n ** 3, r] as const);
  return spinOutcomes;
}

const PAYTABLE = [
  'Per line (bet / 5):',
  '777 x250  BAR x50',
  'BEL  x25  CHR x15',
  'LEM  x15  Mixed x6',
  '2 CHR x5  1 CHR x1',
  'Lines: 3 rows, 2 diags',
].join('\n');

export interface SlotsState {
  grid: SlotGrid | null;
  lastBet: number;
  lastReturn: number;
  wins: LineWin[];
  /** The ★'s luck re-spun a losing spin. */
  lucky?: boolean;
}

export const slots: CasinoGame<SlotsState> = {
  id: 'slots',
  noDealer: true,
  usesLuck: true,
  name: 'Slots',
  blurb: '3x3 reels, 5 lines: rows and diagonals.',
  baseMinBet: 5,
  baseMaxBet: 100,
  betStep: LINES,
  specialPerk: 'Luck: a losing spin sometimes spins again.',
  jackpotRule: '7 7 7 on any line.',
  create: () => ({ grid: null, lastBet: 0, lastReturn: 0, wins: [] }),
  locked: () => false,
  options: () => [
    { id: 'spin', label: 'Spin' },
    { id: 'paytable', label: 'Paytable' },
  ],
  describe(state, _env, highlighted) {
    if (highlighted === 'paytable') return PAYTABLE;
    // Older saves from the single-line version have no grid; treat as fresh.
    if (!state.grid) return 'Tap Spin to play.';
    const grid = state.grid;
    const rows = gridRows(grid);
    const net = state.lastReturn - state.lastBet;
    const lines = state.wins.length ? state.wins.map((w) => `${w.name} x${w.multiplier}`).join(' ') : 'No lines';
    const total = state.lastReturn > 0 ? `${state.lucky ? '★ ' : ''}Pays $${state.lastReturn} (${net >= 0 ? '+' : '-'}$${Math.abs(net)})` : `No win (-$${state.lastBet})`;
    // No blank line between grid and results: with several winning lines
    // this pane is at its ~8-line limit.
    return `${rows.join('\n')}\n${lines}\n${total}`;
  },
  act(state, optionId, env) {
    if (optionId !== 'spin') return { state, bankroll: env.bankroll };
    // The ★'s luck may re-spin a spin that pays back less than the bet.
    const q = secondChanceRate(slotOutcomes(), luckOf(env));
    const { outcome: grid, lucky, first } = drawWithLuck(env.rng, () => spinGrid(env.rng), (g) => spinReturn(g) < 1, q);
    const wins = lineWins(grid);
    const lineBet = env.bet / LINES;
    const returned = wins.reduce((sum, w) => sum + w.multiplier * lineBet, 0);
    // The reels turn and stop left to right; a ★ second chance shows the
    // losing spin first, then spins again.
    const frames = first
      ? [...reelFrames(first), secondChanceFrame(`${gridRows(first).join('\n')}\nNo win`), ...reelFrames(grid)]
      : reelFrames(grid);
    return {
      state: { grid, lastBet: env.bet, lastReturn: returned, wins, lucky },
      bankroll: env.bankroll - env.bet + returned,
      settled: { wagered: env.bet, returned },
      jackpot: wins.some((w) => w.multiplier === 250),
      frames,
    };
  },
};
